import { FC, useEffect, useState } from 'react';
import { ProductDataType } from '@pages/product-analyzer/Product';
import { FilterKeys, PageName } from '@utils/pageConfig/Types';
import { getAbortController } from '@services/utils/OverviewServiceUtils';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { IRootState } from '@state/reducers/Reducers';
import { useSelector } from 'react-redux';
import { getEffectiveProductType } from '@models/SubscribedProductType';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { isEmptyString } from '@utils/StringUtils';
import { byFilterKey } from '@utils/pageConfig/Predicates/SelectedFiltersPredicates';
import saveErrorLog, { ErrorTagEnum } from '@utils/LogUtil';
import {
  getFeatureValueWarningMessage,
  GetFeatureValueWarningMessageParams,
} from '@services/WarningFeatureValue.service';
import { isSelectedPeriodExistInAllMultiCountries } from '@utils/multiCountryUtils/MultiCountryUtils';
import { BaseTimePeriod } from '@services/FiltersTypes';
import { getDefaultValueForDerivablePeriodicity } from '@utils/PeriodUtils';
import { WithAlert } from '@components/design';

interface WarningFeatureValueContainerProps {
  periodicity: Periodicity;
  period: BaseTimePeriod;
  pageName: PageName;
}

const WarningFeatureValueContainer: FC<WarningFeatureValueContainerProps> = ({ periodicity, period, pageName }) => {
  const { appliedFilters, pageType } = usePageConfig();
  const retailerClientState = useSelector((state: IRootState) => state.retailerClient);
  const subscribedProductTypes = useSelector(
    (state: IRootState) => state.subscribedProductTypes.subscribedProductTypes,
  );
  const initialFiltersState = useSelector((state: IRootState) => state.initialFilters);

  const [isDismiss, setIsDismiss] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');

  const effectiveProductType = getEffectiveProductType(subscribedProductTypes, pageName);
  const isShowWarningContainer = !isDismiss && !isEmptyString(warningMessage);

  const getWarningMessage = async () => {
    const appliedCountries = appliedFilters[pageType].selectedFilters.find(byFilterKey(FilterKeys.COUNTRY));
    const appliedProductGroup = appliedFilters[pageType].selectedFilters.find(byFilterKey(FilterKeys.PRODUCT_GROUP));
    if (
      !appliedCountries ||
      !appliedProductGroup ||
      appliedCountries.options.length <= 1 ||
      !isSelectedPeriodExistInAllMultiCountries(
        initialFiltersState,
        period.label,
        periodicity,
        appliedCountries.options,
      )
    ) {
      return;
    }

    try {
      const abortController = getAbortController();
      const requestParameters: GetFeatureValueWarningMessageParams = {
        clientId: retailerClientState.clientId,
        productType: effectiveProductType,
        productDataType: ProductDataType.PerformanceAnalyzer,
        countries: appliedCountries.options.map((country) => country.value.toUpperCase()),
        periodType: getDefaultValueForDerivablePeriodicity(periodicity).toUpperCase(),
        productGroups: appliedProductGroup.options.map((productGroup) => productGroup.value.toUpperCase()),
        appliedFilters: appliedFilters[pageType].selectedFilters,
      };

      const warningMessageFeatureValue = await getFeatureValueWarningMessage(requestParameters, abortController);
      setWarningMessage(warningMessageFeatureValue.warningMessage);
    } catch (error) {
      saveErrorLog(error, `${pageName} - Unable to get multi-country feature value warning message `, {
        category: ErrorTagEnum.ApiError,
        requestPayload: {
          clientId: retailerClientState.clientId,
          country: appliedCountries.options.map((country) => country.value),
          periodType: periodicity.toUpperCase(),
          productGroups: appliedProductGroup.options.map((productGroup) => productGroup.value),
          appliedFilters: appliedFilters[pageType].selectedFilters,
        },
      });
    }
  };

  useEffect(() => {
    // Reset the warning message states
    setIsDismiss(false);
    setWarningMessage('');

    getWarningMessage();
  }, [appliedFilters[pageType].selectedFilters, periodicity]);

  return isShowWarningContainer ? (
    <div className="my-4">
      <WithAlert
        title={warningMessage}
        onClick={() => {
          setIsDismiss(true);
        }}
      />
    </div>
  ) : null;
};

export default WarningFeatureValueContainer;
